import {
  useEffect,
  useRef,
  useCallback,
  useState,
  MutableRefObject,
} from 'react';
import type { PagiflowInstance, PagiflowOptions } from './types';

// @ts-ignore
import PagiflowCore from 'pagiflow';

// ─── Pagiflow loader ─────────────────────────────────────────────────────────
// Resolves the Pagiflow constructor from window (CDN) or require('pagiflow')
function getPagiflow(): ((selector: string | HTMLElement, opts?: PagiflowOptions) => PagiflowInstance) | null {
  if (typeof window !== 'undefined' && (window as any).Pagiflow) {
    return (window as any).Pagiflow;
  }
  return (PagiflowCore as any).default ?? PagiflowCore;
}

function initPagiflowFromElement(
  ctor: (selector: string | HTMLElement, opts?: PagiflowOptions) => PagiflowInstance,
  el: HTMLElement,
  opts: PagiflowOptions
): PagiflowInstance | null {
  const attrValue = `pf-${Math.random().toString(36).slice(2)}`;
  const selector = `[data-pagiflow-react-id="${attrValue}"]`;
  el.setAttribute('data-pagiflow-react-id', attrValue);

  const bySelector = ctor(selector, opts) as PagiflowInstance | null;
  if (!bySelector) {
    el.removeAttribute('data-pagiflow-react-id');
    return null;
  }
  return bySelector;
}

// ─── Hook Return Type ─────────────────────────────────────────────────────────
export interface UsePagiflowReturn {
  /** Attach this ref to the slider track element */
  sliderRef: MutableRefObject<HTMLElement | null>;
  /** Live Pagiflow instance (null until mounted) */
  instance: PagiflowInstance | null;
  /** Current real slide index (0-based, loop-adjusted) */
  currentIndex: number;
  /** Go to a specific slide */
  goTo: (index: number, opts?: { silent?: boolean; instant?: boolean }) => void;
  /** Go to next slide */
  next: () => void;
  /** Go to previous slide */
  prev: () => void;
  /** Play autoplay */
  play: () => void;
  /** Pause autoplay */
  pause: () => void;
  /** Resume from paused state */
  resume: () => void;
  /** Toggle play/pause */
  togglePlayPause: () => void;
  /** Rebuild/reinit with new options */
  setOptions: (opts: Partial<PagiflowOptions>, rebuild?: boolean) => void;
  /** Destroy and reinitialize */
  reInit: (opts?: Partial<PagiflowOptions>) => void;
}

// ─── usePagiflow ─────────────────────────────────────────────────────────────
/**
 * Core hook to initialize and control a Pagiflow slider.
 *
 * @example
 * const { sliderRef, next, prev, currentIndex } = usePagiflow({ loop: true, nav: true });
 * return (
 *   <div ref={sliderRef as React.Ref<HTMLDivElement>}>
 *     <div>Slide 1</div>
 *     <div>Slide 2</div>
 *   </div>
 * );
 */
export function usePagiflow(options: PagiflowOptions = {}): UsePagiflowReturn {
  const sliderRef = useRef<HTMLElement | null>(null);
  const instanceRef = useRef<PagiflowInstance | null>(null);
  const optionsRef = useRef<PagiflowOptions>(options);
  const [currentIndex, setCurrentIndex] = useState(options.startIndex ?? 0);
  const [, forceUpdate] = useState(0);

  // Keep options ref in sync without triggering re-init
  optionsRef.current = options;

  // ─── Initialize ────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;

    const existing = (el as any).pagiflowInstance as PagiflowInstance | null | undefined;
    if (existing && !existing.destroyed) return;

    // Save complete pristine React DOM state
    const originalChildren = Array.from(el.children);
    const originalClasses = el.className;
    const originalStyle = el.getAttribute('style');

    let inst: PagiflowInstance | undefined;

    const Pagiflow = getPagiflow();
    if (!Pagiflow) {
      console.error(
        '[usePagiflow] Pagiflow core not found. Install pagiflow or load it via CDN.'
      );
      return;
    }

    inst = initPagiflowFromElement(
      Pagiflow,
      el,
      optionsRef.current
    ) as PagiflowInstance;

    if (!inst) return;
    (el as any).pagiflowInstance = inst;



    const syncLayout = () => {
      const api = inst as PagiflowInstance & { update?: (syncTarget?: unknown) => void };
      api.update?.();
    };

    const raf1 = window.requestAnimationFrame(() => syncLayout());
    const raf2 = window.requestAnimationFrame(() => syncLayout());
    const timeoutId = window.setTimeout(() => syncLayout(), 80);
    window.addEventListener('load', syncLayout, { once: true });

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => syncLayout());
      resizeObserver.observe(el);
    }

    // Track slide changes
    inst.onSlideChange((idx) => {
      setCurrentIndex(idx);
    });

    instanceRef.current = inst;
    forceUpdate((n) => n + 1); // expose instance to consumers

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(timeoutId);
      window.removeEventListener('load', syncLayout);
      resizeObserver?.disconnect();
      if (instanceRef.current && !instanceRef.current.destroyed) {
        try {
          instanceRef.current.destroy();
        } catch {
          // ignore destroy errors on unmount
        }
      }
      delete (el as any).pagiflowInstance;
      instanceRef.current = null;

      // Fully restore DOM to pristine React state
      el.innerHTML = '';
      originalChildren.forEach(child => el.appendChild(child));
      el.className = originalClasses;
      if (originalStyle !== null) {
        el.setAttribute('style', originalStyle);
      } else {
        el.removeAttribute('style');
      }
      el.removeAttribute('data-pagiflow-react-id');
      delete el.dataset.pagiflowDestroyed;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — init once on mount

  // ─── Stable API ────────────────────────────────────────────────────────────
  const goTo = useCallback(
    (index: number, opts?: { silent?: boolean; instant?: boolean }) => {
      instanceRef.current?.goTo(index, opts);
    },
    []
  );

  const next = useCallback(() => {
    instanceRef.current?.next();
  }, []);

  const prev = useCallback(() => {
    instanceRef.current?.prev();
  }, []);

  const play = useCallback(() => {
    instanceRef.current?.play();
  }, []);

  const pause = useCallback(() => {
    instanceRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    instanceRef.current?.resume();
  }, []);

  const togglePlayPause = useCallback(() => {
    instanceRef.current?.togglePlayPause();
  }, []);

  const setOptions = useCallback(
    (opts: Partial<PagiflowOptions>, rebuild = false) => {
      instanceRef.current?.setOptions(opts, rebuild);
    },
    []
  );

  const reInit = useCallback((opts: Partial<PagiflowOptions> = {}) => {
    instanceRef.current?.reInit(opts);
  }, []);

  return {
    sliderRef,
    instance: instanceRef.current,
    currentIndex,
    goTo,
    next,
    prev,
    play,
    pause,
    resume,
    togglePlayPause,
    setOptions,
    reInit,
  };
}


